import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import ScrollToTopButton from '../../../components/ScrollToTopButton/ScrollToTopButton';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import MedicalSchoolEditorialFooter from './MedicalSchoolEditorialFooter';
import './MedicalSchoolLandingPage.css';

// Reusable horizontal scroll-carousel shell (matches the swipeable card
// pattern used across the admissions matrix, method cards, sample
// question and school case-study sections).
function ScrollCarousel({ children, ariaLabel, trackClassName }) {
  const scrollRef = useRef(null);
  const [dotIndex, setDotIndex] = useState(0);
  const [showPrev, setShowPrev] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const count = React.Children.count(children);

  const cardStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !el.firstChild || !el.firstChild.firstElementChild) return 0;
    const card = el.firstChild.firstElementChild;
    return card.getBoundingClientRect().width + 14;
  }, []);

  const updateUI = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const step = cardStep();
    const activeIndex = step ? Math.round(scrollLeft / step) : 0;
    setDotIndex(activeIndex);
    setShowPrev(scrollLeft > 4);
    setShowNext(scrollLeft < maxScroll - 4);
  }, [cardStep]);

  useEffect(() => {
    updateUI();
    const el = scrollRef.current;
    if (!el) return undefined;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => { updateUI(); ticking = false; });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateUI);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateUI);
    };
  }, [updateUI]);

  const scrollByStep = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * cardStep(), behavior: 'smooth' });
  };

  return (
    <div className="medicalschool-scroll-carousel">
      <button
        type="button"
        className={`medicalschool-carousel-arrow medicalschool-arrow-prev ${showPrev ? 'show' : ''}`}
        onClick={() => scrollByStep(-1)}
        aria-label={`Previous ${ariaLabel}`}
      >
        ←
      </button>
      <button
        type="button"
        className={`medicalschool-carousel-arrow medicalschool-arrow-next ${showNext ? 'show' : ''}`}
        onClick={() => scrollByStep(1)}
        aria-label={`Next ${ariaLabel}`}
      >
        →
      </button>
      <div className="medicalschool-card-scroll" ref={scrollRef}>
        <div className={`medicalschool-card-track ${trackClassName || ''}`}>
          {children}
        </div>
      </div>
      <div className="medicalschool-swipe-dots">
        {Array.from({ length: count }).map((_, i) => (
          <span key={i} className={i === dotIndex ? 'active' : ''} />
        ))}
      </div>
    </div>
  );
}

export default function MedicalSchoolLandingPage() {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const interviewFormats = [
    { school: "Oxford Medicine", format: "Traditional panel, per college", structure: "2 tutorial-style panels", focus: "Data & graph interpretation, sustained scientific follow-up" },
    { school: "Cambridge Medicine", format: "Traditional panel, per college", structure: "1–2 tutorial-style panels", focus: "Applied science reasoning, unseen data, motivation for medicine" },
    { school: "Imperial College London", format: "Multiple Mini Interview (MMI)", structure: "8 stations × 7 minutes", focus: "Ethical scenarios, actor role-play, teamwork under observation" },
    { school: "UCL Medicine", format: "Multiple Mini Interview (MMI)", structure: "6 stations × 8 minutes", focus: "Motivation, ethics, insight into current NHS challenges" },
    { school: "Edinburgh Medicine", format: "Multiple Mini Interview (MMI)", structure: "8 stations × 5 minutes", focus: "Situational judgement, reflection on work experience" },
    { school: "King's College London", format: "Multiple Mini Interview (MMI)", structure: "7 stations × 6 minutes", focus: "Actor communication station, ethical dilemma, prioritisation" },
    { school: "Manchester Medicine", format: "Multiple Mini Interview (MMI)", structure: "8 stations × 5 minutes", focus: "Empathy role-play, resilience under pressure, widening-access motivation" },
    { school: "Southampton Medicine", format: "MMI + written reflection", structure: "Station circuit + short written task", focus: "Reflective writing, ethical reasoning, communication role-play" },
    { school: "Birmingham Medicine", format: "Multiple Mini Interview (MMI)", structure: "9 stations × 5 minutes", focus: "NHS Constitution values, prioritisation, teamwork scenario" },
    { school: "Aston Medicine", format: "Multiple Mini Interview (MMI)", structure: "7 stations × 6 minutes", focus: "Generalist & community motivation, resilience, primary-care ethics" }
  ];

  const structureCards = [
    {
      tag: "Method",
      title: "The IVORI Method",
      meta: "Interact with Pedagogical AI",
      items: [
        "Trained across three foundations spanning biomedical ethics, structured clinical communication and GMC professional standards, so it reasons through a live station or panel the way a real examiner or interviewer actually would",
        "Finds exactly where an ethical framework or a communication approach stops holding up under follow-up, so you correct the actual reasoning gap, not just the final answer",
        <>The world's first <button type="button" className="medicalschool-inline-link medicalschool-link-btn" onClick={() => navigate('/method/medicalschool')}>pedagogical AI model</button>, built on the first <a href="https://ivorilab.com" target="_blank" rel="noopener noreferrer" className="medicalschool-inline-link">Foundation Decision World Model</a>, meaning the feedback comes from a system built for reasoning</>
      ]
    },
    {
      tag: "Behind Foundation II",
      title: "The SPIKES Protocol",
      meta: "Structured Communication Under Pressure",
      items: [
        "A six-step protocol for delivering difficult clinical news, first published for oncology consultations",
        "The same structure IVORI scores a communication station against — setup, perception, invitation, knowledge, emotion, strategy",
        "Widely taught as the standard framework for structuring a hard clinical conversation"
      ]
    },
    {
      tag: "Behind Foundation III",
      title: "GMC Good Medical Practice",
      meta: "The UK's Professional Standard",
      items: [
        "The General Medical Council's own professional-standards document, not an academic paper",
        "Sets out the values every UK doctor is expected to act on — the same values SJT stations score against",
        "The exact framework examiners reach for when scoring a professionalism scenario"
      ]
    },
    {
      tag: "Behind Foundation I",
      title: "Principles of Biomedical Ethics",
      meta: "A Framework, Not a Verdict",
      items: [
        "Beauchamp and Childress's four-principles framework — autonomy, beneficence, non-maleficence, justice",
        "The standard structure taught for reasoning through a clinical ethical dilemma",
        "The same structure IVORI listens for underneath an MMI ethics-station answer"
      ]
    },
    {
      tag: "Three Skills, One Discipline",
      title: "Three Skills, Three Traditions",
      meta: "Trained on All Three Deliberately",
      items: [
        "Ethical Reasoning is trained on Beauchamp and Childress's four-principles framework — scoring the structure of your reasoning, not only which side you land on",
        "Communication Under Observation is trained on the SPIKES protocol — the real structure examiners listen for in a role-play station",
        "Professional Judgement is trained on the GMC's own Good Medical Practice — the values a UK doctor is actually held to"
      ]
    },
    {
      tag: "Foundation I",
      title: "Ethical Reasoning",
      meta: "MMI Ethics Stations & Panel Dilemmas",
      items: [
        "Ethics stations reward a clear framework — autonomy, beneficence, non-maleficence, justice — not just a \"correct\" answer",
        "AI probes each position with a harder version of the same dilemma",
        "Structured feedback on whether your reasoning holds up, not just whether you land a nice-sounding answer"
      ]
    },
    {
      tag: "Foundation II",
      title: "Communication Under Observation",
      meta: "Actor Role-Play & Breaking Bad News",
      items: [
        "Communication stations are marked on active listening, not on saying the \"right\" thing",
        "Practice breaking bad news and handling a distressed or resistant patient",
        "Learn to slow down and check understanding instead of rushing to reassure"
      ]
    },
    {
      tag: "Foundation III",
      title: "Professional Judgement",
      meta: "Situational Judgement & GMC Values",
      items: [
        "Scenarios test how you'd act, not just what you know is technically correct",
        "Drills reward escalation through the right channel over silent compliance or solo heroics",
        "Feedback maps your answer against the values examiners are actually trained to score"
      ]
    },
    {
      tag: "Format",
      title: "Traditional Panel",
      meta: "One Sustained Conversation",
      items: [
        "One extended conversation, usually the same one or two interviewers throughout",
        "All three Foundations can surface within a single dialogue, unannounced",
        "IVORI trains you to hold one continuous line of reasoning as the conversation shifts"
      ]
    },
    {
      tag: "Format",
      title: "The MMI Circuit",
      meta: "A Timed, Station-by-Station Rotation",
      items: [
        "A timed circuit of six to nine stations, each usually isolating one Foundation",
        "A hard reset every five to nine minutes — new scenario, new interviewer",
        "IVORI drills the reset itself: entering fresh, with no residue from the last station"
      ]
    }
  ];

  const schoolCaseStudies = [
    {
      name: "Handsworth Grammar",
      contact: "Handsworth's Medicine Society",
      body: "King Edward VI Handsworth Grammar School for Boys has used IVORI for almost two years, supporting shortlisted Year 12 and 13 candidates with medical school interview preparation once admissions-test results and personal statements place them in front of a shortlisting panel.",
      slug: "handsworth-grammar",
      detailLabel: "Partner Since",
      detailValue: "May 2024",
      detail2Label: "License",
      detail2Value: "Annual"
    },
    {
      name: "Clifton College",
      contact: "Clifton's Team · Careers Department",
      body: "Piloted IVORI's medical school interview simulator with a Year 12 cohort transitioning into Year 13, rehearsing MMI stations and panel interviews for candidates shortlisted across the UK's leading medical schools.",
      slug: "clifton-college",
      detailLabel: "Cohort",
      detailValue: "Year 12 → Year 13",
      detail2Label: "Focus",
      detail2Value: "Medical School Interview Readiness"
    },
    {
      name: "King Edward VI",
      contact: "Active Discussions",
      body: "Building on the existing relationship with Handsworth Grammar, IVORI is in active discussions with the wider King Edward VI Foundation about extending medical school interview preparation across its family of Birmingham grammar schools.",
      slug: "king-edward-vi-foundation",
      detailLabel: "Status",
      detailValue: "In Discussion",
      detail2Label: "Scope",
      detail2Value: "Foundation-Wide"
    },
    {
      name: "Grammar Schools",
      contact: "School Group",
      body: "Deployed across schools within the grammar school group for medical school interview practice, supporting shortlisted Year 12 and 13 students across both MMI and traditional panel formats.",
      slug: "grammar-schools",
      detailLabel: "Group",
      detailValue: "State Grammar Schools",
      detail2Label: "Focus",
      detail2Value: "Medical School Interview Prep"
    },
    {
      name: "Independent Schools",
      contact: "School Group",
      body: "Deployed across schools within the independent school group for medical school interview practice, supporting shortlisted Year 12 and 13 students with station-by-station and panel-specific rehearsal.",
      slug: "independent-schools",
      detailLabel: "Group",
      detailValue: "Independent Schools",
      detail2Label: "Focus",
      detail2Value: "Medical School Interview Prep"
    }
  ];

  const faqs = [
    {
      q: "What is the medical school interview, and why does it matter?",
      a: [
        "Most UK medical schools shortlist applicants for either a Multiple Mini Interview (MMI) — a timed circuit of short stations testing ethics, communication and judgement — or a traditional panel interview, still used at Oxford and Cambridge. Rather than testing recalled knowledge, both formats assess how candidates reason through ethical dilemmas, communicate under observation, and demonstrate the values medical schools and the NHS expect of a future doctor.",
        "This live-reasoning discipline is exactly what IVORI's medical school interview tutoring and prep is built to train, rather than a bank of questions with a single correct answer."
      ]
    },
    {
      q: "Does IVORI cover the MMI format specifically, or only traditional panels?",
      a: [
        "Both, and deliberately not treated as interchangeable. IVORI's simulator runs full multi-station MMI circuits — ethics, communication, situational judgement and prioritisation stations timed the way the real circuit runs — for the eight leading medical schools that use the format.",
        "Oxford and Cambridge still interview in traditional subject panels rather than a station circuit, so IVORI runs a separate tutorial-style panel simulation for those two, built around sustained follow-up on one scenario rather than short, separately-timed stations."
      ]
    },
    {
      q: "What is IVORI's pedagogical method for medical school interview prep, and who's behind it?",
      a: [
        "Every simulation, follow-up and feedback pattern is reviewed by admissions specialists and clinicians, alongside educators with formal pedagogical training. That review process is what lets IVORI explain not just whether an answer held up, but exactly where it stopped holding up.",
        "IVORI trains on three foundations, one per interview skill: Beauchamp and Childress's four-principles framework for ethical reasoning, the SPIKES protocol for structured clinical communication, and the GMC's own Good Medical Practice for professional judgement. Underneath all three sits IVORI's own foundation decision world model — proprietary technology researched and pioneered in-house by IVORI Lab, and, we believe, the world's first pedagogical AI model built on this kind of foundation.",
        "It's IVORI Lab's proprietary foundation decision world model — a system trained to reason about decision-making under uncertainty, rather than simply predict text. IVORI's Medical School products sit on top of this underlying model, using its proprietary training methodology to run live follow-up pressure and score a candidate's reasoning the way a real examiner or interviewer would."
      ]
    },
    {
      q: "Which medical schools does IVORI's simulator cover?",
      a: ["IVORI covers station and panel simulations across the UK's ten leading medical schools: Oxford, Cambridge, Imperial College London, UCL, Edinburgh, King's College London, Manchester, Southampton, Birmingham and Aston, alongside general ethics, communication and situational judgement practice used across UK medical admissions more broadly."]
    },
    {
      q: "Can I cancel my subscription at any time?",
      a: ["Yes. All subscriptions can be managed or cancelled in one click from your account profile with zero hidden fees or commitments."]
    }
  ];

  const host = window.location.hostname.toLowerCase();
  const onIvoriEdu = host === 'medicalschool.ivoriedu.com';
  const onIvoriAiEdu = host === 'medicalschool.edu.ivoriai.com';
  const medicalSchoolUrl = onIvoriEdu
    ? 'https://medicalschool.ivoriedu.com/'
    : onIvoriAiEdu
      ? 'https://medicalschool.edu.ivoriai.com/'
      : (host.includes('medicalschool') ? 'https://medicalschool.edu.ivoriai.com/' : 'https://ivoriai.com/edu/medicalschool');
  const eduHomeUrl = onIvoriEdu
    ? 'https://ivoriedu.com'
    : 'https://ivoriai.com/';

  return (
    <div className="medicalschool-landing-root">
      <SeoMeta
        title="IVORI — Medical School Interview Prep"
        description="AI-powered medical school interview prep — live MMI station and panel simulations, ethics drilling and mock interviews from IVORI Edu."
        keywords="medical school interview prep, mmi prep, medicine interview prep, oxford medicine interview, cambridge medicine interview, imperial medicine mmi, ucl medicine mmi, medical school mock interview, mmi station practice, panel interview medicine, ivori medical school, aston medical school mmi, birmingham medicine mmi, nhs core values interview, breaking bad news station, spikes protocol medicine interview, medical ethics 4 pillars"
        url={medicalSchoolUrl}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Course",
                "name": "Medical School Interview Preparation",
                "description": "AI-powered medical school interview prep from IVORI Edu, trained on three foundations spanning biomedical ethics, structured clinical communication and GMC professional standards — live MMI station and panel simulations, ethical-reasoning drilling, and mock interviews across the UK's top 10 medical schools.",
                "provider": {
                  "@type": "Organization",
                  "name": "ivori",
                  "url": eduHomeUrl
                },
                "url": medicalSchoolUrl,
                "courseMode": "online",
                "inLanguage": "en-GB",
                "isAccessibleForFree": false,
                "educationalLevel": "A-Level / Undergraduate Admissions",
                "teaches": [
                  "MMI station reasoning under live follow-up pressure",
                  "Ethical reasoning as a trainable four-principles structure",
                  "Structured clinical communication under observation",
                  "Professional judgement and situational-judgement practice across ten UK medical schools"
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": faqs.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a.join(' ') }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ivoriai.com" },
                  { "@type": "ListItem", "position": 2, "name": "Medical School Interview Preparation", "item": medicalSchoolUrl }
                ]
              }
            ]
          })
        }}
      />
      <Header variant="exam-landing" />

      {/* HERO */}
      <section className="medicalschool-hero-section">
        <div className="medicalschool-container medicalschool-hero-content">
          <span className="medicalschool-mono-eyebrow">IVORI Edu</span>
          <h1 className="medicalschool-hero-title">Master the Medical School Interview.</h1>
          <p className="medicalschool-hero-stat">Realistic AI-simulated MMI stations and traditional panel interviews across the UK's top 10 medical schools, with structured feedback after every mock.</p>
          <p className="medicalschool-hero-tagline">Train the clinical judgement medical schools actually score.</p>
          <button type="button" className="medicalschool-hero-cta" onClick={() => navigate('/pricing?package=medicine')}>See plans</button>
        </div>
      </section>

      {/* INTERVIEW FORMAT MATRIX */}
      <section id="medicalschool-matrix" className="medicalschool-editorial-section">
        <div className="medicalschool-container">
          <div className="medicalschool-editorial-section-head">
            <span className="medicalschool-mono-eyebrow">School-By-School Breakdown</span>
            <h2>UK Medical School Interview Formats for 2026/2027 Entry</h2>
            <p>Oxford Medicine interview prep, Cambridge Medicine interview prep, Imperial College London MMI prep, UCL Medicine MMI prep and mock interviews across the UK's ten leading medical schools — from Oxford &amp; Cambridge's tutorial-style panels to the multi-station MMIs run at Birmingham and Aston.</p>
          </div>

          <ScrollCarousel ariaLabel="medical school">
            {interviewFormats.map((row, idx) => (
              <div className="medicalschool-matrix-card" key={idx}>
                <div className="medicalschool-matrix-uni">{row.school}</div>
                <div className="medicalschool-matrix-row"><span>Interview Format</span><b>{row.format}</b></div>
                <div className="medicalschool-matrix-row"><span>Structure</span><b>{row.structure}</b></div>
                <div className="medicalschool-matrix-row medicalschool-score-cell"><span>Primary Focus</span><b>{row.focus}</b></div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* THE IVORI METHOD */}
      <section className="medicalschool-editorial-section">
        <div className="medicalschool-container">
          <div className="medicalschool-editorial-section-head">
            <span className="medicalschool-mono-eyebrow">How It Works</span>
            <h2>The IVORI Method</h2>
            <p>Three skills a personal statement and predicted grades never test on paper — and the three the medical school interview is actually built around</p>
          </div>

          <ScrollCarousel ariaLabel="skill">
            {structureCards.map((card, idx) => (
              <div className="medicalschool-structure-card" key={idx}>
                <span className="medicalschool-mono-tag">{card.tag}</span>
                <h3>{card.title}</h3>
                <div className="medicalschool-structure-meta">{card.meta}</div>
                <ul>
                  {card.items.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* SAMPLE INTERVIEW */}
      <section className="medicalschool-editorial-section">
        <div className="medicalschool-container">
          <div className="medicalschool-editorial-section-head">
            <span className="medicalschool-mono-eyebrow">Interactive Practice</span>
            <h2>Improve Your Score</h2>
            <p>See how IVORI runs a live MMI station or panel interview across all ten of the UK's leading medical schools — including the follow-up pressure a real interviewer or examiner would apply</p>
          </div>

          <ScrollCarousel ariaLabel="card">
            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Oxford Medicine · Opening Question</span>
              <div className="medicalschool-passage">
                "A new blood test identifies a disease with 95% sensitivity and 90% specificity, in a population where the disease has a prevalence of 1%."
              </div>
              <h3>If a patient tests positive, how confident should we be that they actually have the disease?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">What happens to that confidence if we only test patients already showing symptoms?</div>
              <div className="medicalschool-option">How would you explain this result to a worried patient in plain language?</div>
              <div className="medicalschool-option">Should this test be rolled out for population-wide screening?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates recognise this as a positive predictive value problem, not a simple readout of the test's own accuracy — and reason explicitly about how prevalence changes the answer.</p>
              <p>IVORI checks whether the candidate correctly predicts that predictive value rises sharply in a symptomatic, higher-prevalence population, rather than repeating the sensitivity and specificity figures unchanged.</p>
              <p>Translating a statistical result into something a patient can actually understand is exactly the composure Oxford tutors are listening for.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Cambridge Medicine · Opening Question</span>
              <div className="medicalschool-passage">
                "A patient with smoking-related lung disease tells you he intends to keep smoking, and asks why he should bother with treatment at all."
              </div>
              <h3>How would you respond to him, and how would you justify continuing to treat him?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Live Update — New Information</span>
              <div className="medicalschool-passage">
                "Suppose he tells you he's tried to quit twice before and relapsed within a week each time. Does that change how you'd respond?"
              </div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>A rehearsed answer repeats the same advice regardless of the new detail. A strong candidate reframes relapse as part of a normal quitting trajectory rather than a reason to withdraw treatment or judgement.</p>
              <p>IVORI flags answers that lecture the patient rather than explore his reasons for continuing to smoke, and rewards candidates who separate clinical duty of care from personal approval of a patient's choices.</p>
              <p>This non-judgemental, evidence-grounded reasoning is exactly what Cambridge supervisors probe for beneath the ethics of the scenario.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Imperial College London · MMI Ethics Station</span>
              <div className="medicalschool-passage">
                "A 16-year-old asks you not to tell her parents she has started taking the contraceptive pill."
              </div>
              <h3>Do you agree to keep this confidential?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">What if she tells you her boyfriend is 19?</div>
              <div className="medicalschool-option">Does your answer change if her parents call the surgery directly and ask?</div>
              <div className="medicalschool-option">What would make you consider breaking confidentiality here?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates reach for the Fraser Guidelines rather than a blanket "always confidential" or "always tell the parents" position, and check the girl's understanding and the age gap with her partner before deciding.</p>
              <p>IVORI checks whether the candidate identifies a genuine safeguarding trigger — a materially older partner — as the point where confidentiality would need to be reconsidered, not just gestured at in the abstract.</p>
              <p>This is the same competency-and-boundaries reasoning Imperial's communication and ethics stations are built to test in a single seven-minute rotation.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">UCL Medicine · MMI Station</span>
              <div className="medicalschool-passage">
                "The NHS is currently facing record waiting lists for elective surgery."
              </div>
              <h3>What would you prioritise if you were asked to reduce a hospital's waiting list with a fixed budget?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">How would you decide between funding more operations now versus more staff long-term?</div>
              <div className="medicalschool-option">What if reducing one specialty's list means another specialty's list grows?</div>
              <div className="medicalschool-option">How do you factor in patients who have already waited the longest?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates acknowledge this is a genuine trade-off rather than proposing a costless fix, and distinguish short-term throughput gains from sustainable long-term capacity.</p>
              <p>IVORI checks whether the candidate engages with fairness — clinical urgency versus time already waited — rather than defaulting to a single rule like "first come, first served."</p>
              <p>Reasoning honestly about scarcity and trade-offs in the NHS is exactly the systems-level insight UCL's interviewers are trained to score.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Edinburgh Medicine · MMI Station</span>
              <div className="medicalschool-passage">
                "During a placement, you notice a fellow student has copied part of a patient write-up from another student's work."
              </div>
              <h3>What do you do?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">What if the student is a close friend who asks you to keep quiet?</div>
              <div className="medicalschool-option">Does it matter whether the copied section affected patient care?</div>
              <div className="medicalschool-option">What would you do if you'd already raised it with them privately and nothing changed?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates raise the issue directly with the student first, then escalate through the proper academic channel if it continues — rather than staying silent out of loyalty or reporting immediately without any conversation.</p>
              <p>IVORI checks whether the candidate treats academic integrity as inseparable from patient safety, since the same honesty is expected in clinical documentation later.</p>
              <p>This graduated, values-led response is exactly the professionalism judgement Edinburgh's situational stations are designed to surface.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">King's College London · MMI Communication Station</span>
              <div className="medicalschool-passage">
                An actor plays a patient who has just been told, by another clinician, that a routine scan found something unexpected requiring further tests.
              </div>
              <h3>Enter the room and respond to how the patient is feeling.</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">The patient asks you directly whether it's cancer — what do you say?</div>
              <div className="medicalschool-option">The patient goes quiet and stops responding — how do you continue?</div>
              <div className="medicalschool-option">The patient asks you to just tell them what to do — how do you respond?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates acknowledge the patient's emotion before moving to information, and resist guessing a diagnosis they aren't in a position to confirm.</p>
              <p>IVORI checks whether the candidate uses silence well rather than filling it with reassurance, and whether they involve the patient in next steps rather than simply directing them.</p>
              <p>Active listening scored moment-to-moment, not a rehearsed script, is precisely what King's actor-based stations are built to test.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Manchester Medicine · MMI Station</span>
              <div className="medicalschool-passage">
                "You are the only person on your ward round to notice a colleague seems exhausted and has started making small errors."
              </div>
              <h3>What do you do?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">What if the colleague is more senior than you?</div>
              <div className="medicalschool-option">What if they ask you not to say anything, just this once?</div>
              <div className="medicalschool-option">How would your answer change if a patient had already been affected?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates raise a concern about patient safety and colleague wellbeing directly, and don't let seniority or a personal request excuse silence once an error has actually reached a patient.</p>
              <p>IVORI checks whether the candidate frames this as compassion for a struggling colleague as well as a safety duty, rather than treating it as purely disciplinary.</p>
              <p>Balancing empathy with accountability under real pressure is the resilience Manchester's widening-access mission is built to select for.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Southampton Medicine · MMI &amp; Written Reflection</span>
              <div className="medicalschool-passage">
                "Reflect on a time you observed a healthcare professional communicate difficult information."
              </div>
              <h3>What did you learn from watching them, and what would you do differently?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">What made that moment difficult to watch, specifically?</div>
              <div className="medicalschool-option">Would your answer differ if you had been the patient rather than the observer?</div>
              <div className="medicalschool-option">How do you know your proposed change would actually help?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong reflections go beyond describing what happened to naming a specific communication choice and its effect — rather than a generic "they were very professional."</p>
              <p>IVORI checks whether the candidate proposes a genuinely different approach and reasons about why it might help, rather than simply praising or criticising what they saw.</p>
              <p>This depth of structured reflection is exactly what Southampton's written task is designed to surface alongside the MMI circuit.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Birmingham Medicine · MMI Station</span>
              <div className="medicalschool-passage">
                "You are part of a ward team asked to prioritise which of three patients gets the one remaining side room, for non-clinical comfort reasons."
              </div>
              <h3>How would you approach making this decision as part of the team?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">What if one patient's family becomes upset with your decision?</div>
              <div className="medicalschool-option">How do you balance dignity for one patient against fairness to the other two?</div>
              <div className="medicalschool-option">What if you personally disagree with the team's final decision?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates ground the decision in the NHS Constitution's values — compassion, dignity, respect — rather than treating it as a purely logistical allocation problem.</p>
              <p>IVORI checks whether the candidate keeps the decision collaborative under family pressure, and whether they can voice a respectful disagreement within the team rather than either staying silent or overriding it.</p>
              <p>Weighing dignity, fairness and teamwork together under time pressure is exactly what Birmingham's nine-station circuit is built to score.</p>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Aston Medicine · MMI Station</span>
              <div className="medicalschool-passage">
                "A patient in a rural GP practice says he'd rather 'tough it out' than come in for check-ups, because the nearest hospital is over an hour away."
              </div>
              <h3>How would you approach this conversation as his GP?</h3>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">Follow-Up Pressure</span>
              <div className="medicalschool-option">What if transport, not attitude, is the real barrier?</div>
              <div className="medicalschool-option">How would your approach differ from a hospital consultant's?</div>
              <div className="medicalschool-option">What does this tell you about why you want to work in generalist or community medicine?</div>
            </div>

            <div className="medicalschool-qcard-panel">
              <span className="medicalschool-mono-tag">What IVORI Listens For</span>
              <p>Strong candidates probe for the practical barrier — distance, transport, time off work — before assuming reluctance is simply personal preference.</p>
              <p>IVORI checks whether the candidate reasons like a generalist, thinking about continuity of care and the whole person rather than a single presenting complaint.</p>
              <p>This community-minded, resilience-driven motivation is precisely what Aston's newer, widening-access-focused MMI is designed to identify.</p>
            </div>
          </ScrollCarousel>
        </div>
      </section>

      {/* SCHOOL CASE STUDIES */}
      <section className="medicalschool-editorial-section">
        <div className="medicalschool-container">
          <div className="medicalschool-editorial-section-head">
            <span className="medicalschool-mono-eyebrow">Used in Schools</span>
            <h2>Trusted by UK Schools</h2>
            <p>IVORI Edu's medical school interview coaching runs from Handsworth Grammar medicine interview prep and a Clifton College medicine interview pilot, to King Edward VI Foundation medicine interview readiness, Grammar Schools medicine interview practice and Independent Schools medicine interview practice nationwide.</p>
          </div>

          <ScrollCarousel ariaLabel="school">
            {schoolCaseStudies.map((school, idx) => (
              <div className="medicalschool-school-card" key={idx}>
                <h3>{school.name}</h3>
                <div className="medicalschool-school-contact">{school.contact}</div>
                <p>{school.body}</p>
                <p className="medicalschool-school-more">
                  Find out more about{' '}
                  <button type="button" className="medicalschool-inline-link medicalschool-link-btn" onClick={() => navigate(`/case-studies/${school.slug}`)}>
                    {school.name}
                  </button>
                </p>
                <div className="medicalschool-school-detail">
                  {school.detailLabel}: <b>{school.detailValue}</b> · {school.detail2Label}: <b>{school.detail2Value}</b>
                </div>
              </div>
            ))}
          </ScrollCarousel>
        </div>
      </section>

      {/* FAQ */}
      <section className="medicalschool-editorial-section medicalschool-faq-editorial">
        <div className="medicalschool-container">
          <div className="medicalschool-editorial-section-head">
            <span className="medicalschool-mono-eyebrow">Got Questions?</span>
            <h2>FAQs</h2>
            <p>Find answers on prep tiers, mock formats and how IVORI's live feedback works</p>
          </div>

          <div className="medicalschool-faq">
            {faqs.map((faq, index) => (
              <div key={index} className={`medicalschool-faq-item ${activeFaq === index ? 'open' : ''}`}>
                <div className="medicalschool-faq-q" onClick={() => toggleFaq(index)}>
                  <span>{faq.q}</span>
                  <span className="medicalschool-faq-plus">+</span>
                </div>
                <div className="medicalschool-faq-a">
                  {faq.a.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MedicalSchoolEditorialFooter />
      <ScrollToTopButton />
    </div>
  );
}
